import { useMemo, useState } from 'react';
import { Button, Header, Icon, Label, Message, Segment, Table } from 'semantic-ui-react';
import { describe } from '../approvals.js';
import { DEFAULT_STATUS, buildHome, expectedPeriod, filterHome, homeYears, managersIn, ownedBy, sortHome } from '../home.js';
import StatusSelect from './StatusSelect.jsx';
import Tabs from './Tabs.jsx';

const when = new Intl.DateTimeFormat('en-GB', { dateStyle: 'long' });
const FREQ = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annual' };
/* An annual report is followed through the year like a quarterly one, but is not issued (so not approved) until January. */
const notIssued = (r) => (r.frequency === 'annual' ? 'Issued in January' : 'Pending');
const UNIT = { monthly: 'month', quarterly: 'quarter', annual: 'quarter' };
const NEXT = { finance: 'Finance approval', account_manager: 'Account Manager approval' };

/* A column heading that sorts the table when clicked (click again to reverse; a third click goes back to the default order). */
function SortHead({ id, sort, onSort, children, className }) {
  const on = sort.key === id;
  return (
    <Table.HeaderCell scope="col" className={className} aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="sortbtn" onClick={() => onSort(id)} title={`Sort by ${String(children).toLowerCase()}`}>
        {children}<Icon name={on ? (sort.dir === 'asc' ? 'caret up' : 'caret down') : 'sort'} className={on ? 'sorton' : 'sortoff'} aria-hidden="true" />
      </button>
    </Table.HeaderCell>
  );
}

/* Reports waiting for approval: ID, client, frequency, account manager and an Open button that goes to the report. */
function AwaitingTable({ id, title, hint, rows, onOpen, sort, onSort }) {
  return (
    <section aria-labelledby={`${id}-heading`} className="block">
      <Header as="h3" id={`${id}-heading`} size="small">{title} <Label size="small" circular>{rows.length}</Label></Header>
      {hint && <p className="hint">{hint}</p>}
      {!rows.length ? <Message info role="status" size="small">Nothing is waiting for approval here.</Message> : (
        <div className="gridwrap" tabIndex={0} role="region" aria-label={`${title} (scrollable)`}>
          <Table celled compact collapsing unstackable className="datagrid hometable">
            <caption className="visually-hidden">{title}</caption>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell scope="col"><span className="visually-hidden">Open</span></Table.HeaderCell>
                <SortHead id="id" sort={sort} onSort={onSort} className="lab">Settlement Report ID</SortHead>
                <SortHead id="partner" sort={sort} onSort={onSort}>Client Name</SortHead>
                <SortHead id="frequency" sort={sort} onSort={onSort}>Frequency</SortHead>
                <SortHead id="period" sort={sort} onSort={onSort}>Period</SortHead>
                <SortHead id="manager" sort={sort} onSort={onSort}>Account Manager</SortHead>
                <SortHead id="finance" sort={sort} onSort={onSort}>Waiting for</SortHead>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((r) => (
                <Table.Row key={`${r.id}/${r.expected.label}`}>
                  <Table.Cell><Button type="button" basic compact size="small" content="Open" aria-label={`Open ${r.id} ${r.expected.label}`} onClick={() => onOpen(r.dueReport, r.expected.sel)} /></Table.Cell>
                  <Table.HeaderCell scope="row" className="lab">{r.id}</Table.HeaderCell>
                  <Table.Cell>{r.partner || '–'}</Table.Cell>
                  <Table.Cell>{FREQ[r.frequency]}</Table.Cell>
                  <Table.Cell>{r.expected.label}</Table.Cell>
                  <Table.Cell>{r.manager || '–'}</Table.Cell>
                  <Table.Cell>{NEXT[r.awaitingLevel]}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </section>
  );
}

/* The reports awaiting action by the signed-in person.
 * Finance: every unapproved report. Account Manager: their own unapproved reports, then everyone else's in a second table. */
function Awaiting({ rows, user, onOpen, sort, onSort }) {
  const awaiting = rows.filter((r) => r.awaiting);
  if (!user) return <Message warning role="note">Your account is not set up as a Finance user or an Account Manager, so nothing is waiting for you.</Message>;
  if (user.role === 'finance') {
    return <AwaitingTable id="aw-finance" title="Unapproved reports" hint="Every report that has been reported but is not yet approved by both Finance and the Account Manager." rows={awaiting} onOpen={onOpen} sort={sort} onSort={onSort} />;
  }
  if (user.role === 'account_manager') {
    const mine = awaiting.filter((r) => ownedBy(user, r.report));
    const others = awaiting.filter((r) => !ownedBy(user, r.report));
    return (
      <>
        <AwaitingTable id="aw-mine" title="Your unapproved reports" hint="Reports where you are the account manager. You can give Account Manager approval once Finance has approved." rows={mine} onOpen={onOpen} sort={sort} onSort={onSort} />
        <AwaitingTable id="aw-others" title="Unapproved reports for other account managers" rows={others} onOpen={onOpen} sort={sort} onSort={onSort} />
      </>
    );
  }
  return <Message warning role="note">Your account has no Finance or Account Manager role, so nothing is waiting for you.</Message>;
}

/* The Home filters and sorting are kept in session storage, so they are still there when the person comes back to this screen. */
const KEY = 'srs_home_filters';
const MINE = '__mine';                                   // the Account manager filter's "My reports"
const DEFAULTS = { year: null, tab: 'all', manager: null, frequency: '', status: DEFAULT_STATUS, sort: { key: '', dir: 'asc' } };
function loadPrefs() {
  try { return { ...DEFAULTS, ...JSON.parse(window.sessionStorage.getItem(KEY) || '{}') }; } catch { return { ...DEFAULTS }; }
}
function savePrefs(p) { try { window.sessionStorage.setItem(KEY, JSON.stringify(p)); } catch { /* storage blocked */ } }

/* Where every report has got to: the most recent reporting period against what each report has reached, for the chosen year (default: this year).
 * Two tabs: the status of every report, and the reports awaiting action by the signed-in person. */
export default function HomeTab({ reports, config = [], approvals, user, onOpen, today = new Date() }) {
  const years = useMemo(() => homeYears(reports, today), [reports, today]);
  const [prefs, setPrefs] = useState(loadPrefs);
  const set = (patch) => setPrefs((p) => { const next = { ...p, ...patch }; savePrefs(next); return next; });
  const year = years.includes(prefs.year) ? prefs.year : today.getFullYear();
  const { tab, manager, frequency, status, sort } = prefs;
  const everyone = useMemo(() => buildHome(reports, approvals, today, year, config), [reports, approvals, today, year, config]);
  const managers = useMemo(() => managersIn(everyone), [everyone]);
  const isAM = !!user && user.role === 'account_manager' && !!String(user.account_manager || '').trim();
  const defaultMgr = isAM ? MINE : '';                                  // an Account Manager starts on their own reports; anyone can choose All account managers
  const chosen = manager === null || manager === undefined ? defaultMgr : manager;
  const mgr = chosen === MINE ? (isAM ? MINE : '') : managers.includes(chosen) ? chosen : '';
  const byOwner = useMemo(() => filterHome(everyone, { manager: mgr === MINE ? '' : mgr, mine: mgr === MINE ? user : null, frequency }), [everyone, mgr, frequency, user]);   // before the Status filter, so the counts below cover every status
  const rows = useMemo(() => sortHome(filterHome(byOwner, { status }), sort.key, sort.dir), [byOwner, status, sort]);
  // the Awaiting tab splits mine / others itself, so "My reports" must not hide the others there
  const awaitingRows = useMemo(() => sortHome(mgr === MINE ? filterHome(everyone, { frequency }) : byOwner, sort.key, sort.dir), [everyone, byOwner, mgr, frequency, sort]);
  const onSort = (key) => set({ sort: sort.key !== key ? { key, dir: 'asc' } : sort.dir === 'asc' ? { key, dir: 'desc' } : { key: '', dir: 'asc' } });
  const filtered = !!(mgr !== defaultMgr || frequency || status !== DEFAULTS.status);
  const behind = byOwner.filter((r) => r.state === 'behind').length;
  const missing = byOwner.filter((r) => r.state === 'missing').length;
  const unapproved = byOwner.filter((r) => r.state === 'current' && r.stage !== 'complete' && r.frequency !== 'annual').length;
  const thisYear = year >= today.getFullYear();
  return (
    <section aria-labelledby="home-heading">
      <Header as="h2" id="home-heading" className="titlebar">
        <Icon name="home" />
        <Header.Content>Home<Header.Subheader>{thisYear ? `Status of the latest reports as at ${when.format(today)}` : `Status of the reports at the end of ${year}`}</Header.Subheader></Header.Content>
      </Header>
      <div className="toolbar" role="group" aria-label="Choose the year">
        <label htmlFor="home-year">Year</label>
        <select id="home-year" className="ui dropdown" value={year} onChange={(e) => set({ year: Number(e.target.value) })}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <label htmlFor="home-manager">Account manager</label>
        <select id="home-manager" className="ui dropdown" value={mgr} onChange={(e) => set({ manager: e.target.value })}>
          {isAM && <option value={MINE}>My reports ({user.account_manager})</option>}
          <option value="">All account managers</option>
          {managers.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <label htmlFor="home-frequency">Frequency</label>
        <select id="home-frequency" className="ui dropdown" value={frequency} onChange={(e) => set({ frequency: e.target.value })}>
          <option value="">All frequencies</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="annual">Annual</option>
        </select>
        <StatusSelect id="home-status" value={status} onChange={(v) => set({ status: v })} />
        {filtered && <Button type="button" basic compact size="small" icon="close" labelPosition="left" content="Reset filters" onClick={() => set({ manager: null, frequency: '', status: DEFAULTS.status })} />}
      </div>
      <Tabs idPrefix="home" label="Home sections" value={tab} onChange={(t) => set({ tab: t })} tabs={[['all', 'All reports'], ['mine', 'Awaiting my action']]}>
        {tab === 'mine' ? <Awaiting rows={awaitingRows} user={user} onOpen={onOpen} sort={sort} onSort={onSort} /> : (
          <>
            {thisYear
              ? <p className="hint">Monthly reports should be reported to <strong>{expectedPeriod('monthly', today).label}</strong>; quarterly reports to <strong>{expectedPeriod('quarterly', today).label}</strong>; annual reports are followed like quarterly ones (to <strong>{expectedPeriod('annual', today).label}</strong>) but are not issued until January. A quarterly report falls due only in January, April, July and October.</p>
              : <p className="hint">For {year}, monthly reports should run to December, quarterly and annual reports to Q4 (annual reports are issued in January).</p>}
            {!rows.length ? <Segment placeholder><Header icon><Icon name="folder open outline" />{filtered && everyone.length ? 'No reports match the filters.' : `There are no reports for ${year}.`}</Header></Segment> : (
              <>
                <dl className="stats" aria-label="Report status">
                  <div><dt>Reports</dt><dd>{byOwner.length}</dd></div>
                  <div className="ok"><dt>Up to date</dt><dd>{byOwner.length - behind - missing}</dd></div>
                  <div className={behind ? 'bad' : 'ok'}><dt>Behind</dt><dd>{behind}</dd></div>
                  <div className={missing ? 'bad' : 'ok'}><dt>Missing</dt><dd>{missing}</dd></div>
                  <div className={unapproved ? 'warn' : 'ok'}><dt>Up to date, not fully approved</dt><dd>{unapproved}</dd></div>
                </dl>
                <div className="gridwrap" tabIndex={0} role="region" aria-label="Latest report status (scrollable)">
                  <Table celled compact collapsing unstackable className="datagrid hometable">
                    <caption className="visually-hidden">The latest report for each report ID, the period due and its Finance and AM approval status.</caption>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell scope="col"><span className="visually-hidden">Open</span></Table.HeaderCell>
                        <SortHead id="id" sort={sort} onSort={onSort} className="lab">Report ID</SortHead>
                        <SortHead id="partner" sort={sort} onSort={onSort}>Partner</SortHead>
                        <SortHead id="manager" sort={sort} onSort={onSort}>Account manager</SortHead>
                        <SortHead id="frequency" sort={sort} onSort={onSort}>Frequency</SortHead>
                        <SortHead id="period" sort={sort} onSort={onSort}>Reporting period</SortHead>
                        <SortHead id="status" sort={sort} onSort={onSort}>Status</SortHead>
                        <SortHead id="finance" sort={sort} onSort={onSort}>Finance approval</SortHead>
                        <SortHead id="account_manager" sort={sort} onSort={onSort}>AM approval</SortHead>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rows.map((r) => (
                        <Table.Row key={`${r.id}/${r.report ? r.report.report_year : 'none'}`} className={r.state === 'behind' ? 'bad-differs' : r.state === 'missing' ? 'bad-missing' : undefined}>
                          <Table.Cell>
                            {r.state === 'missing' ? null : r.dueReport
                              ? <Button type="button" basic compact size="small" content="Open" aria-label={`Open ${r.id}`} onClick={() => onOpen(r.dueReport, r.expected.sel)} />
                              : <Button type="button" basic compact size="small" content="Open" aria-label={`Open ${r.id}`} onClick={() => onOpen(r.report, r.reportedTo.sel)} />}
                          </Table.Cell>
                          <Table.HeaderCell scope="row" className="lab">{r.id}</Table.HeaderCell>
                          <Table.Cell>{r.partner || '–'}</Table.Cell>
                          <Table.Cell>{r.manager || '–'}</Table.Cell>
                          <Table.Cell>{FREQ[r.frequency]}</Table.Cell>
                          <Table.Cell>{r.expected.label}</Table.Cell>
                          <Table.Cell>
                            {r.state === 'current'
                              ? <Label basic size="small" color="green"><Icon name="check circle" />Up to date</Label>
                              : r.state === 'missing'
                              ? <Label basic size="small" color="red" title="This report is expected but none has been received"><Icon name="question circle" />Missing</Label>
                              : <Label basic size="small" color="orange" title={`Reported to ${r.reportedTo.label}`}><Icon name="exclamation triangle" />{r.behind} {UNIT[r.frequency]}{r.behind === 1 ? '' : 's'} behind</Label>}
                          </Table.Cell>
                          {r.state === 'behind' || r.state === 'missing'
                            ? <Table.Cell colSpan={2} className="hint">{r.state === 'missing' ? `No report received (due for ${r.expected.label})` : `Not yet reported for ${r.expected.label}`}</Table.Cell>
                            : <>
                              <Table.Cell className="approvalcell">{describe(r.finance) || <span className="hint">{notIssued(r)}</span>}</Table.Cell>
                              <Table.Cell className="approvalcell">{describe(r.account_manager) || <span className="hint">{notIssued(r)}</span>}</Table.Cell>
                            </>}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
                <p className="hint">Each report is shown at its latest as-at date. Finance and AM approval are for the reporting period shown; a report that has not got that far shows how far it has (hover the status); “Missing” is an expected report (settlement_report_config) with no report at all; “Open” goes to the report. Click a column heading to sort by it (click again to reverse, a third time for the default order).</p>
              </>
            )}
          </>
        )}
      </Tabs>
    </section>
  );
}
